/**
 * Os tokens visuais das telas de acesso.
 *
 * Ficam fora de `TelaDeAcesso.tsx` porque aquele arquivo exporta componentes: misturar
 * constantes com componentes quebra o recarregamento a quente do Vite, e o lint avisa.
 */

import branding from '../../config/branding';

/**
 * A serifa é do mockup, e é ela que separa as telas de acesso do resto do sistema — que é todo
 * em sans-serif. Fica restrita ao painel de marca e ao título do cartão.
 */
export const SERIFA = 'Cambria, Georgia, "Times New Roman", serif';

/**
 * As cores de apoio saem de liderasst.com.br, das mesmas variáveis que o site usa
 * (`--ink`, `--muted`, `--line`). Não entram no `branding` porque são a paleta neutra do
 * cartão claro — quem troca a marca troca o navy e o destaque, não o cinza do texto.
 */
export const TINTA = '#12213d';
export const TINTA_SUAVE = '#607085';
export const LINHA = '#dbe5e8';
/** Campo do site: fundo quase branco, borda fria. */
export const CAMPO_FUNDO = '#f8fbfb';
export const CAMPO_BORDA = '#d8e2e6';
/** O teal escuro que o site usa em link — o claro não tem contraste sobre branco. */
export const TEAL_LINK = '#078f8b';

const { loginNavy, loginAccent, loginAccentContrast } = branding.colors;

export { loginAccent, loginAccentContrast, loginNavy };

/** `#0a1c3d` + 0.94 → `rgba(10, 28, 61, 0.94)`. Aceita a cor como o env a escreve. */
export function comAlfa(hex: string, alfa: number): string {
  const limpo = hex.replace('#', '');
  const cheio =
    limpo.length === 3
      ? limpo
          .split('')
          .map((c) => c + c)
          .join('')
      : limpo;
  const n = Number.parseInt(cheio, 16);
  if (!Number.isFinite(n) || cheio.length !== 6) return hex;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alfa})`;
}

/** Véu sobre a foto: sem ele o texto branco cai em cima de rosto claro e some. */
export const VEU = [
  `linear-gradient(90deg, ${comAlfa(loginNavy, 0.99)} 0%, ${comAlfa(loginNavy, 0.94)} 31%, ${comAlfa(
    loginNavy,
    0.54
  )} 58%, ${comAlfa(loginNavy, 0.2)} 100%)`,
  `linear-gradient(0deg, ${comAlfa(loginNavy, 0.88)} 0%, transparent 50%)`,
].join(', ');

/**
 * Campo com rótulo em cima, não o label flutuante padrão do MUI.
 *
 * Altura, raio, fundo e borda são os do formulário de liderasst.com.br. A altura cede um pouco
 * em tela baixa: é parte do que faz a tela caber sem rolagem.
 */
export const estiloDoCampo = {
  '& .MuiOutlinedInput-root': {
    height: 'clamp(46px, 6.6vh, 51px)',
    borderRadius: '10px',
    bgcolor: CAMPO_FUNDO,
    '& fieldset': { borderColor: CAMPO_BORDA },
    '&:hover fieldset': { borderColor: loginAccent },
    '&.Mui-focused fieldset': { borderColor: loginAccent, borderWidth: 1 },
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: `0 0 0 4px ${comAlfa(loginAccent, 0.16)}`,
  },
  // O cartão é claro por decisão de layout, mas o tema do app segue o sistema e pode estar
  // no escuro: sem cor explícita, o texto digitado sai branco sobre branco.
  '& .MuiInputBase-input': { color: TINTA },
  '& .MuiInputBase-input::placeholder': { color: '#94a1b5', opacity: 1 },
  // O Chrome pinta o campo preenchido automaticamente com a cor DELE — que no tema escuro do
  // navegador vira um retângulo quase preto em cima do cartão branco. A sombra interna é a
  // única forma de repintar: `background-color` é ignorado nesse pseudo-estado.
  '& .MuiInputBase-input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 100px ${CAMPO_FUNDO} inset !important`,
    WebkitTextFillColor: `${TINTA} !important`,
    caretColor: TINTA,
    borderRadius: '10px',
  },
  '& .MuiFormHelperText-root': { mt: 0.25, mb: 0 },
} as const;

/**
 * O botão principal do cartão: é o `.primary-button` do site — teal sólido com tinta escura em
 * cima, não degradê com texto branco.
 */
export const estiloDoBotao = {
  minHeight: 'clamp(48px, 7vh, 56px)',
  borderRadius: '11px',
  color: loginAccentContrast,
  fontSize: '1.02rem',
  fontWeight: 850,
  textTransform: 'none',
  bgcolor: loginAccent,
  boxShadow: `0 10px 26px ${comAlfa(loginAccent, 0.2)}`,
  transition: 'transform .2s, box-shadow .2s, filter .2s',
  '&:hover': {
    bgcolor: loginAccent,
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 32px ${comAlfa(loginAccent, 0.28)}`,
    filter: 'saturate(1.08)',
  },
  '&.Mui-disabled': { bgcolor: loginAccent, color: loginAccentContrast, opacity: 0.6 },
} as const;

